import { Translation } from "@shared/types/translation";
import { useLocale } from "@translations/locale-context";
import { Icon } from "@ui/components/icon/icon";
import { Tooltip } from "@uxf/ui/tooltip";
import Image from "next/legacy/image";
import React from "react";

interface Pillar {
    description: Translation;
    title: Translation;
}
type Pillars = Pillar[];

const PILLARS: Pillars = [
    {
        title: {
            cs: "Expertní znalost",
            en: "Experience and expert knowledge",
        },
        description: {
            cs: "Neděláme to poprvé. Máme vysokou a\u00a0dlouhodobou znalost na\u00a0poli e-commerce, online byznysu a\u00a0zvyšování obchodní výkonnosti. Využíváme naše odborné znalosti k\u00a0pochopení kontextu a\u00a0specifik uživatelského chování.",
            en: "It's not the first time we've done this. We have extensive and long-term knowledge in the field of e-commerce, online business and increasing business performance. We use our expertise to understand the context and specifics of user behavior.",
        },
    },
    {
        title: {
            cs: "Uživatelská data",
            en: "User data",
        },
        description: {
            cs: "Detailně zkoumáme kontextově bohaté informace, které získáváme testováním s\u00a0uživateli, analyzujeme jejich reakce a\u00a0vzorce chování a\u00a0identifikujeme tak klíčové problémy ve\u00a0struktuře a\u00a0uspořádání rozhraní.",
            en: "We scrutinize the context-rich information we gain from testing with users, analyze their reactions and behavior patterns, and thus identify key issues in the structure and layout of the interface.",
        },
    },
    {
        title: {
            cs: "Kvantitativní data",
            en: "Quantitative data",
        },
        description: {
            cs: "Díky číslům a\u00a0analytickým nástrojům kvantifikujeme výkon různých aspektů uživatelského rozhraní, identifikujeme trendy a\u00a0statisticky významné vzory v\u00a0chování. Integrace měřitelných metod je klíčová v\u00a0objektivitě návrhů řešení. ",
            en: "With numbers and analytical tools, we quantify the performance of various aspects of the user interface, identify trends and statistically significant patterns in behavior. The integration of measurable methods is key in the objectivity of solution proposals.",
        },
    },
];

const ContentCs = () => (
    <>
        <h2 className="pb-4 text-h2Mobile font-bold text-uxfViolet sm:text-medium xl:text-h5">
            Naše audity stojí na <span className="text-uxfOrange">3&nbsp;hlavních pilířích</span>
        </h2>
        <p className="text-small1 text-uxfMuted75 sm:text-body">
            Riziko vašich promrhaných investic minimalizujeme metodicky, osvědčenou triangulací.
        </p>
    </>
);

const ContentEn = () => (
    <>
        <h2 className="pb-4 text-h2Mobile font-bold text-uxfViolet sm:text-medium xl:text-h5">
            Our audits are based on <span className="text-uxfOrange">3&nbsp;main pillars</span>
        </h2>
        <p className="text-small1 text-uxfMuted75 sm:text-body">
            We minimize the risk of your wasted investments methodically, through proven triangulation.
        </p>
    </>
);
export const PillarSection = () => {
    const locale: "cs" | "en" = useLocale();
    const cs = "/static/landing-pages/3pillars.png";
    const en = "/static/landing-pages/3pillars-eng.png";
    return (
        <div className="container lg:flex lg:items-center">
            <div className="px-5 md:px-0 lg:py-12">
                {locale === "en" ? <ContentEn /> : <ContentCs />}
                <div className="max-w-[690px] pt-12">
                    <ul className="flex max-w-3xl flex-col space-y-10">
                        {PILLARS.map(({ description, title }, index) => (
                            <li key={index} className="flex">
                                <span className="pr-4 text-body font-medium text-uxfWhite75 sm:text-medium">
                                    0{index + 1}
                                </span>
                                <Tooltip
                                    content={description[locale]}
                                    placement="bottom"
                                    className="bg-uxfMuted75 px-4 py-5 text-white sm:text-small2 md:hidden"
                                >
                                    <div className="items-center max-md:flex max-md:space-x-2">
                                        <h3 className="text-body font-medium text-uxfVioletDark sm:text-medium">
                                            {title[locale]}
                                        </h3>
                                        <Icon name="infoInverted" size={16} className="text-uxfMuted75 md:hidden" />
                                        <p className="text-small pt-6 text-body text-uxfMuted75 max-md:hidden">
                                            {description[locale]}
                                        </p>
                                    </div>
                                </Tooltip>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="mx-5 py-6 pt-12 text-center lg:p-0">
                <Image
                    src={locale === "en" ? en : cs}
                    alt={
                        locale === "en"
                            ? "Three pillars of ux audit from UXF: experience, quantitative data, user data"
                            : "Tři pilíře ux auditu od UXF: zkušenosti, kvalitativní data, uživatelská data"
                    }
                    width={405}
                    height={405}
                />
            </div>
        </div>
    );
};
